import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Container,
  Header,
  Icon,
  Segment,
  Message,
  Label,
  Grid,
  Statistic,
  Table,
  Confirm,
  Divider,
  Card,
  Progress,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
// 样式已内联化，移除CSS文件引用

const CoursewareIntegration = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState({
    enabled: false,
    base_url: '',
    api_key: '',
    timeout: 5,
    cache_ttl: 10,
    default_group: 'default',
    preload_batch_size: 100,
    refresh_interval: 60,
  });
  const [status, setStatus] = useState({
    isConnected: false,
    lastSyncTime: null,
    totalUsers: 0,
    cachedUsers: 0,
    syncProgress: 0,
    isSyncing: false,
    error_message: '',
  });
  const [cacheData, setCacheData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingCache, setEditingCache] = useState(null);

  useEffect(() => {
    loadConfig();
    loadStatus();
    loadCacheData();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await API.get('/api/courseware/config');
      if (response.data.success) {
        setConfig(response.data.data || {});
      }
    } catch (error) {
      console.error('加载配置失败:', error);
    }
  };

  const loadStatus = async () => {
    try {
      const response = await API.get('/api/courseware/status');
      if (response.data.success) {
        const data = response.data.data || {};
        // 修复字段名映射：后端使用下划线，前端使用驼峰
        setStatus({
          isConnected: data.is_connected || false,
          lastSyncTime: data.last_sync_time || null,
          totalUsers: data.total_users || 0,
          cachedUsers: data.cached_users || 0,
          syncProgress: data.sync_progress || 0,
          isSyncing: data.is_syncing || false,
          error_message: data.error_message || '',
        });
      }
    } catch (error) {
      console.error('加载状态失败:', error);
    }
  };

  const loadCacheData = async () => {
    try {
      setLoading(true);
      const response = await API.get('/api/courseware/cache');
      if (response.data.success) {
        // 修复：从 response.data.data.items 获取数组数据
        setCacheData(response.data.data?.items || []);
      }
    } catch (error) {
      console.error('加载缓存数据失败:', error);
      setCacheData([]); // 确保在错误时设置为空数组
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const response = await API.post('/api/courseware/test');
      if (response.data.success) {
        showSuccess('连接测试成功');
        loadStatus();
      } else {
        showError(response.data.message || '连接测试失败');
      }
    } catch (error) {
      showError('连接测试失败: ' + error.message);
    }
  };

  const handleSyncUsers = async () => {
    try {
      setStatus(prev => ({ ...prev, isSyncing: true }));
      const response = await API.post('/api/courseware/sync');
      if (response.data.success) {
        // 同步任务已启动，等待一段时间后检查状态
        showSuccess('同步任务已启动，正在检查状态...');
        
        // 等待3秒后检查状态
        setTimeout(async () => {
          try {
            const statusResponse = await API.get('/api/courseware/status');
            if (statusResponse.data.success) {
              const statusData = statusResponse.data.data || {};
              
              // 检查是否有错误信息
              if (statusData.error_message && statusData.error_message.trim() !== '') {
                showError('同步失败: ' + statusData.error_message);
              } else if (!statusData.is_connected) {
                showError('同步失败: 无法连接到课件平台');
              } else {
                showSuccess('用户同步成功');
              }
            } else {
              showError('无法获取同步状态');
            }
          } catch (statusError) {
            showError('检查同步状态失败: ' + statusError.message);
          }
        }, 3000);
        
        loadStatus();
        loadCacheData();
      } else {
        showError(response.data.message || '同步失败');
      }
    } catch (error) {
      showError('同步失败: ' + error.message);
    } finally {
      setStatus(prev => ({ ...prev, isSyncing: false }));
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await API.delete('/api/courseware/cache');
      if (response.data.success) {
        showSuccess('缓存清理成功');
        loadStatus();
        loadCacheData();
      } else {
        showError(response.data.message || '清理失败');
      }
    } catch (error) {
      showError('清理失败: ' + error.message);
    }
  };

  const handleDeleteCache = async () => {
    try {
      const response = await API.delete(`/api/courseware/cache/${editingCache.teacher_id}`);
      if (response.data.success) {
        showSuccess('缓存删除成功');
        setConfirmOpen(false);
        setEditingCache(null);
        loadCacheData();
      } else {
        showError(response.data.message || '删除失败');
      }
    } catch (error) {
      showError('删除失败: ' + error.message);
    }
  };

  const renderConnectionStatus = () => {
    if (status.isConnected) {
      return <Label color="green" size="large"><Icon name="check circle" />已连接</Label>;
    }
    return <Label color="red" size="large"><Icon name="x circle" />未连接</Label>;
  };

  const renderSyncStatus = () => {
    if (status.isSyncing) {
      return (
        <div>
          <Label color="blue" size="large"><Icon name="sync" loading />同步中</Label>
          <Progress percent={status.syncProgress} indicating size="small" style={{ marginTop: '5px' }} />
        </div>
      );
    }
    return <Label color="grey" size="large"><Icon name="clock outline" />空闲</Label>;
  };

  const renderErrorMessage = () => {
    if (status.error_message) {
      return (
        <Message negative>
          <Message.Header>
            <Icon name="warning circle" />
            连接错误
          </Message.Header>
          <p style={{ marginBottom: '10px' }}>{status.error_message}</p>
          <p style={{ fontSize: '0.9em', color: '#666' }}>
            <strong>可能的原因：</strong>
            <br />• 课件平台系统未启动或无法访问
            <br />• 网络连接问题
            <br />• API配置错误（URL、密钥等）
            <br />• 防火墙或代理设置阻止连接
          </p>
          <p style={{ fontSize: '0.9em', color: '#666' }}>
            <strong>建议操作：</strong>
            <br />• 检查课件平台系统是否正常运行
            <br />• 验证配置中的URL和API密钥是否正确
            <br />• 尝试点击"测试连接"按钮验证连接状态
          </p>
        </Message>
      );
    }
    return null;
  };

  return (
    <div style={{
      padding: '20px 24px 40px',
      backgroundColor: '#ffffff',
      marginTop: '-15px',
      maxWidth: '1600px',
      marginLeft: 'auto',
      marginRight: 'auto'
    }}>
      <Card fluid style={{
        height: '100%',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        border: 'none',
        borderRadius: '16px',
        padding: '8px'
      }}>
        <Card.Content>
          <Card.Header style={{
            color: '#2B3674',
            fontSize: '1.2em',
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: '600',
            gap: '12px'
          }}>{t('courseware.title')}</Card.Header>

      {/* 状态概览 */}
      <Grid columns={4} stackable style={{ marginBottom: '20px' }}>
        <Grid.Column>
          <Statistic>
            <Statistic.Value>{renderConnectionStatus()}</Statistic.Value>
            <Statistic.Label>连接状态</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic>
            <Statistic.Value>{status.totalUsers}</Statistic.Value>
            <Statistic.Label>总用户数</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic>
            <Statistic.Value>{status.cachedUsers}</Statistic.Value>
            <Statistic.Label>缓存用户数</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic>
            <Statistic.Value>{renderSyncStatus()}</Statistic.Value>
            <Statistic.Label>同步状态</Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid>

      {/* 错误信息显示 */}
      {renderErrorMessage()}

      {/* 配置卡片 */}
      <Card fluid>
        <Card.Content>
          <Card.Header>
            <Icon name="settings" />
            集成配置
          </Card.Header>
          <Card.Description>
            <Grid columns={2} stackable>
              <Grid.Column>
                <p><strong>启用状态:</strong> {config.enabled ? '已启用' : '已禁用'}</p>
                <p><strong>API地址:</strong> {config.base_url || '未配置'}</p>
                <p><strong>超时时间:</strong> {config.timeout}秒</p>
              </Grid.Column>
              <Grid.Column>
                <p><strong>缓存时间:</strong> {config.cache_ttl}分钟</p>
                <p><strong>默认分组:</strong> {config.default_group}</p>
                <p><strong>刷新间隔:</strong> {config.refresh_interval}分钟</p>
              </Grid.Column>
            </Grid>
          </Card.Description>
        </Card.Content>
        <Card.Content extra>
          <Button.Group>
            <Button onClick={handleTestConnection}>
              <Icon name="wifi" />
              测试连接
            </Button>
          </Button.Group>
        </Card.Content>
      </Card>

      <Divider />

      {/* 操作按钮 */}
      <Segment>
        <Button.Group>
          <Button primary onClick={handleSyncUsers} loading={status.isSyncing}>
            <Icon name="sync" />
            同步用户
          </Button>
          <Button onClick={handleClearCache}>
            <Icon name="trash" />
            清理缓存
          </Button>
          <Button onClick={loadCacheData}>
            <Icon name="refresh" />
            刷新数据
          </Button>
        </Button.Group>
      </Segment>

      {/* 缓存数据表格 */}
      <Segment>
        <Header as="h3">
          <Icon name="database" />
          缓存数据
        </Header>
        
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>用户ID</Table.HeaderCell>
              <Table.HeaderCell>用户组</Table.HeaderCell>
              <Table.HeaderCell>偏好模型</Table.HeaderCell>
              <Table.HeaderCell>学校</Table.HeaderCell>
              <Table.HeaderCell>学科组</Table.HeaderCell>
              <Table.HeaderCell>更新时间</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {Array.isArray(cacheData) && cacheData.map((item) => (
              <Table.Row key={item.teacher_id}>
                <Table.Cell>{item.teacher_id}</Table.Cell>
                <Table.Cell>{item.group_name}</Table.Cell>
                <Table.Cell>{item.preferred_model || '-'}</Table.Cell>
                <Table.Cell>{item.school_name || '-'}</Table.Cell>
                <Table.Cell>{item.subject_name || '-'}</Table.Cell>
                <Table.Cell>{new Date(item.cache_time * 1000).toLocaleString()}</Table.Cell>
                <Table.Cell>
                  <Button.Group size="mini">
                    <Button
                      icon
                      onClick={() => {
                        setEditingCache(item);
                        setConfirmOpen(true);
                      }}
                      title="删除缓存"
                    >
                      <Icon name="trash" />
                    </Button>
                  </Button.Group>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>

        {(!Array.isArray(cacheData) || cacheData.length === 0) && !loading && (
          <Message info>
            <Message.Header>暂无缓存数据</Message.Header>
            <p>点击"同步用户"按钮开始同步数据</p>
          </Message>
        )}
      </Segment>

        </Card.Content>
      </Card>

      {/* 删除确认框 */}
      <Confirm
        open={confirmOpen}
        header="删除缓存"
        content={`确定要删除用户 "${editingCache?.teacher_id}" 的缓存数据吗？`}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleDeleteCache}
        cancelButton="取消"
        confirmButton="删除"
      />
    </div>
  );
};

export default CoursewareIntegration; 